import React, { useState, useEffect } from "react";
import { Box, Typography, Snackbar, Alert, CircularProgress, TextField, MenuItem } from "@mui/material";
import FormComponentResponse from "./FormComponentResponse";
import ToolBarResponse from "./ToolBarResponse";
import dataProvider from "../../../components/dataProvider";
import { useParams, useNavigate } from "react-router-dom";

const BASE_WIDTH = 895;
const BASE_HEIGHT = 1200;
const PAGE_ASPECT_RATIO = BASE_HEIGHT / BASE_WIDTH;
const PAGE_WIDTH = "237mm";

const FormResponseBuilder = ({
  customer: customerProp,
  onSave,
  saving = false,
  onBack,
  onSuccess,
  mode = "create",
  formResponseId: formResponseIdProp,
}) => {
  const { id: customerIdFromParams, formResponseId: formResponseIdFromParams } = useParams();
  const formResponseId = formResponseIdProp || formResponseIdFromParams;
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(customerProp || null);
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [imageFiles, setImageFiles] = useState(new Map()); // Para almacenar archivos de imagen
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [loadingForms, setLoadingForms] = useState(true);
  const [loadingForm, setLoadingForm] = useState(false);
  const [loadingResponse, setLoadingResponse] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [responseName, setResponseName] = useState("");

  // Fetch customer si no viene por props
  useEffect(() => {
    if (!customerProp && customerIdFromParams) {
      setLoadingCustomer(true);
      dataProvider("GET_ONE", "enterprise/customers", { id: customerIdFromParams })
        .then((res) => {
          setCustomer(res.data);
          setLoadingCustomer(false);
        })
        .catch(() => setLoadingCustomer(false));
    } else if (customerProp) {
      setCustomer(customerProp);
    }
  }, [customerProp, customerIdFromParams]);

  // Cargar forms para seleccionar (solo en modo create)
  useEffect(() => {
    if (mode === "create") {
      setLoadingForms(true);
      dataProvider("GET_LIST", "enterprise/forms", { pagination: { page: 1, perPage: 100 } }).then((res) => {
        setForms(res.data || []);
        setLoadingForms(false);
      });
    }
  }, [mode]);

  // Cargar respuesta y formulario en modo edit/view
  useEffect(() => {
    if (mode === "edit" || mode === "view") {
      if (!formResponseId) return;
      setLoadingResponse(true);
      dataProvider("GET_ONE", "enterprise/form-responses", { id: formResponseId }).then((res) => {
        const response = res.data;
        setAnswers(response.answers || {});
        setSelectedFormId(response.formId);
        setResponseName(response.name || "");
        // Cargar el formulario asociado
        setLoadingForm(true);
        dataProvider("GET_ONE", "enterprise/forms", { id: response.formId }).then((res2) => {
          setForm(res2.data);
          setLoadingForm(false);
          setLoadingResponse(false);
        });
      });
    }
  }, [mode, formResponseId]);

  // Cargar formulario seleccionado en modo create
  useEffect(() => {
    if (mode === "create" && selectedFormId) {
      setLoadingForm(true);
      dataProvider("GET_ONE", "enterprise/forms", { id: selectedFormId }).then((res) => {
        setForm(res.data);
        setAnswers({});
        setLoadingForm(false);
      });
    }
  }, [selectedFormId, mode]);

  const handleChange = (componentId, value) => {
    // Si el valor es una imagen (base64), extraer y guardar el archivo
    if (typeof value === "string" && value.startsWith("data:image/")) {
      // Convertir base64 a blob y guardarlo
      const base64Data = value.split(",")[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "image/png" });

      // Crear archivo mock para FormData
      const file = new File([blob], `response_image_${componentId}.png`, { type: "image/png" });
      setImageFiles((prev) => new Map(prev.set(componentId, file)));
    }

    setAnswers((prev) => ({ ...prev, [componentId]: value }));
  };

  const handleSave = async () => {
    if (!form?._id && !form?.id) return;
    try {
      if (onSave) {
        await onSave({
          formId: form._id || form.id,
          customerId: customer?._id,
          answers,
          name: responseName,
          formResponseId: mode === "edit" ? formResponseId : undefined,
          imageFiles, // Pasar archivos de imagen
        });
      } else {
        // Crear FormData para enviar con imágenes
        const formData = new FormData();

        // Limpiar answers: si es base64, dejar vacío; si es URL, dejar igual
        const cleanedAnswers = { ...answers };
        for (const [componentId, value] of Object.entries(cleanedAnswers)) {
          if (typeof value === "string" && value.startsWith("data:image/")) {
            cleanedAnswers[componentId] = "";
          }
        }

        const responseData = {
          formId: form._id || form.id,
          customerId: customer?._id,
          answers: cleanedAnswers,
          name: responseName,
        };

        formData.append("responseData", JSON.stringify(responseData));

        // Agregar archivos de imagen
        for (const [componentId, file] of imageFiles) {
          formData.append(`response_image_${componentId}`, file);
        }

        if (mode === "edit") {
          await fetch(`${process.env.REACT_APP_API_URL}/enterprise/form-responses/${formResponseId}`, {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: formData,
          });
        } else {
          await fetch(`${process.env.REACT_APP_API_URL}/enterprise/form-responses`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: formData,
          });
        }
      }
      setSnackbar({ open: true, message: "Respuesta guardada exitosamente", severity: "success" });
      if (onSuccess) onSuccess();
      if (mode === "create") {
        navigate(`/enterprise/customers/${customerIdFromParams}/forms`);
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Error al guardar respuesta", severity: "error" });
    }
  };

  const handleDownload = (format) => {
    if (format === "json") {
      const responseData = {
        formId: form?._id,
        formName: form?.name,
        customerId: customer?._id,
        answers,
        metadata: {
          answeredAt: new Date().toISOString(),
        },
      };
      const dataStr = JSON.stringify(responseData, null, 2);
      const dataUri = "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
      const exportFileDefaultName = `respuesta_${form?.name?.replace(/\s+/g, "_")}.json`;
      const linkElement = document.createElement("a");
      linkElement.setAttribute("href", dataUri);
      linkElement.setAttribute("download", exportFileDefaultName);
      linkElement.click();
    } else if (format === "pdf") {
      // Solo permitir descarga de PDF si estamos en modo edit o view y tenemos un formResponseId
      if (mode === "edit" || mode === "view") {
        if (formResponseId) {
          const token = localStorage.getItem("token");
          const url = `${process.env.REACT_APP_API_URL}/enterprise/form-responses/${formResponseId}/pdf`;

          fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
            .then((response) => {
              if (response.ok) {
                return response.blob();
              }
              throw new Error("Error al descargar PDF");
            })
            .then((blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `respuesta_${form?.name?.replace(/\s+/g, "_")}.pdf`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            })
            .catch((error) => {
              console.error("Error:", error);
              setSnackbar({ open: true, message: "Error al descargar PDF", severity: "error" });
            });
        } else {
          setSnackbar({ open: true, message: "No se puede descargar PDF sin guardar primero", severity: "warning" });
        }
      } else {
        setSnackbar({ open: true, message: "Guarda la respuesta antes de descargar PDF", severity: "warning" });
      }
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Determinar si los campos deben ser solo lectura
  const readOnly = mode === "view";

  if (loadingCustomer) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100vh", bgcolor: "white", display: "flex", flexDirection: "column" }}>
      <ToolBarResponse
        forms={mode === "create" ? forms : []}
        selectedFormId={selectedFormId}
        onSelectForm={mode === "create" ? setSelectedFormId : undefined}
        formName={form?.name || ""}
        onSaveResponse={mode !== "view" ? handleSave : undefined}
        onDownload={handleDownload}
        onBack={onBack}
        saving={saving}
        loadingForms={mode === "create" ? loadingForms : false}
        customer={customer}
        mode={mode}
        responseName={responseName}
        onResponseNameChange={setResponseName}
      />
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            position: "relative",
            bgcolor: "#f1f3f4",
            p: 3,
          }}
        >
          {(mode === "edit" || mode === "view") && (loadingResponse || loadingForm) ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress size={60} />
            </Box>
          ) : mode === "create" && loadingForm ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress size={60} />
            </Box>
          ) : !form ? (
            <Box
              sx={{
                width: PAGE_WIDTH,
                minHeight: `calc(${PAGE_WIDTH} * ${PAGE_ASPECT_RATIO})`,
                margin: "0 auto",
                bgcolor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                border: "1px solid #e0e0e0",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Selecciona un formulario desde la cabecera
              </Typography>
            </Box>
          ) : (
            <>
              {form.pages.map((page, index) => (
                <Box
                  key={page.id || index}
                  sx={{
                    marginBottom: "80px !important",
                    position: "relative",
                    width: PAGE_WIDTH,
                    minHeight: `calc(${PAGE_WIDTH} * ${PAGE_ASPECT_RATIO})`,
                    margin: "0 auto 80px auto",
                    bgcolor: "white",
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    border: "1px solid #e0e0e0",
                    "&:last-child": {
                      marginBottom: "0 !important",
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -30,
                      left: "50%",
                      transform: "translateX(-50%)",
                      bgcolor: "white",
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                      fontSize: "0.875rem",
                      color: "text.secondary",
                      zIndex: 10,
                    }}
                  >
                    Página {index + 1} de {form.pages.length}
                  </Box>
                  <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
                    {page.components.map((component) => (
                      <Box
                        key={component.id}
                        sx={{
                          position: "absolute",
                          left: `${component.rndPosition?.x || 0}%`,
                          top: `${component.rndPosition?.y || 0}%`,
                          width: `${component.rndPosition?.width || 20}%`,
                          height: `${component.rndPosition?.height || 10}%`,
                        }}
                      >
                        <FormComponentResponse
                          component={component}
                          value={answers[component.id]}
                          onChange={(val) => handleChange(component.id, val)}
                          readOnly={readOnly}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FormResponseBuilder;
